import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useInRouterContext, useLocation } from 'react-router-dom';
import { useTenant } from './TenantContext';
import useLanguageSwitch from '../i18n/useLanguageSwitch';
import { isPortalI18nActive } from '../i18n';
import {
  MARKETING_EMAIL,
  MARKETING_LANGUAGES,
  MARKETING_LEGAL_ADDRESS,
  MARKETING_LEGAL_ENTITY,
  MARKETING_PHONE,
  MARKETING_QUOTE_API_LABEL,
  handoffLanguage,
  marketingAboutHref,
  marketingBase,
  marketingFooterCopy,
  marketingFooterGroups,
  marketingPrivacyChoicesHref,
} from '../util/marketingSite';
import { normalizeThemePreset } from '../util/themePreset';
import { useMarketingLinkProps } from '../util/stepNavigation';

/**
 * The marketing site's footer, reproduced for the booking portal.
 *
 * A transcription of the site's SSR `foot()` in `aperio/site/build/shell.js`:
 * the logo + tagline block with its About link, the Product / Business /
 * Company link columns, the bottom row with the copyright, the two published
 * contact channels and the language pill, and under it the line for AI
 * assistants (`p.foot-ai`) and the plan-underwriting disclosure
 * (`p.foot-disclosure`). Styling is the site's own `footer` /
 * `.foot` rules, namespaced `apr-` and scoped to the preset in
 * `public/aperio/theme.css`.
 *
 * It speaks the language the visitor was reading the site in (handoffLanguage,
 * the same value that lights the language pill), with that language's labels
 * and links into that language's tree — the footer the site itself printed on
 * the page the visitor just left. English, the site's default, otherwise.
 *
 * Renders nothing at all unless the tenant has BOTH a marketingSiteUrl and a
 * non-default preset, so every existing tenant — Go Oil included — gets no
 * footer element in the DOM and no change of any kind.
 *
 * Two controls of the site's footer are left out because they drive site-only
 * machinery that does not exist here: the "Privacy settings" button (opens the
 * site's consent manager) and the Default / High contrast toggle (site.js).
 * Both would be dead buttons in the portal. Everything else is carried over,
 * including the accessibility statement, which AODA wants reachable from every
 * page.
 *
 * "Your privacy choices", which the site adds to its bottom row once an
 * advertising id is set, IS carried over — as a link to the site's
 * /privacy#choices (which opens the consent settings with advertising off),
 * behind MARKETING_PRIVACY_CHOICES in util/marketingSite.ts (on since the
 * advertising ids were set, 2026-09-25).
 */
/**
 * The bottom row's language pill once the portal runs its own translations:
 * the same markup as the site's, switching this page in place (QA #26).
 * A component of its own because it needs the router; the footer itself is
 * also rendered outside one (tests).
 */
function PortalLanguagePill({ label }: { label: string }) {
  const switcher = useLanguageSwitch();
  return (
    <div className="apr-seg apr-seg--lang" role="group" aria-label={label}>
      {MARKETING_LANGUAGES.map((lang) => (
        <a
          key={lang.code}
          className={cx({ 'apr-seg__on': lang.code === switcher.lang })}
          aria-current={lang.code === switcher.lang ? 'true' : undefined}
          href={switcher.hrefFor(lang.code)}
          hrefLang={lang.hrefLang}
          lang={lang.code}
          aria-label={lang.name}
          onClick={(event) => switcher.switchTo(lang.code, event)}
        >
          {lang.label}
        </a>
      ))}
    </div>
  );
}

export default function MarketingFooter() {
  // Re-renders the footer when the portal's language changes.
  const { t } = useTranslation();
  const tenant = useTenant();
  // Mid-booking, links out open in a new tab (util/stepNavigation).
  const outLink = useMarketingLinkProps();
  // Same outside-a-router guard as useMarketingLinkProps.
  const inRouter = useInRouterContext();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const onFleetDashboard = (inRouter ? useLocation().pathname : '').startsWith('/fleet');
  const base = marketingBase(tenant?.marketingSiteUrl);
  const preset = normalizeThemePreset(tenant?.themePreset);

  if (!base || preset === 'default') return null;

  const activeLang = handoffLanguage();
  const copy = marketingFooterCopy(activeLang);
  const groups = marketingFooterGroups(base, activeLang);
  const home = MARKETING_LANGUAGES.find((l) => l.code === activeLang);
  const choices = marketingPrivacyChoicesHref(base, activeLang);
  // The site's footer line for AI assistants (shell.js foot(), util.js
  // aiQuoteLine), in the footer's language: the sentence around the address,
  // which stays plain text in a translate="no" span, as on the site.
  const [aiBefore, aiAfter = ''] = t('footer.aiQuote', {
    lng: activeLang,
    url: '\u0000',
  }).split('\u0000');

  return (
    // The portal's <html> says lang="en"; a French or Spanish footer says so
    // itself (WCAG 3.1.2), as the site's own page does on its <html>.
    <footer
      className="apr-footer"
      lang={home && home.code !== 'en' ? home.hrefLang : undefined}
    >
      <div className="apr-footer__wrap">
        <div className="apr-foot">
          <div>
            <a
              className="apr-logo notranslate"
              translate="no"
              href={`${base}${home ? home.path : ''}`}
            >
              <span className="apr-logo-mark" aria-hidden="true">A</span>
              Aperio
            </a>
            <p>{copy.tagline}</p>
            <a
              href={marketingAboutHref(base, activeLang)}
              target={outLink.target}
              rel={outLink.rel}
            >
              {copy.about}
            </a>
          </div>
          {groups.map((group) => (
            <div key={group.heading}>
              <p className="apr-foot-h">{group.heading}</p>
              {group.items.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  target={outLink.target}
                  rel={outLink.rel}
                >
                  {item.label}
                </a>
              ))}
              {group.contact && (
                <>
                  <a href={`tel:${MARKETING_PHONE}`}>{copy.phoneHuman}</a>
                  <a href={`mailto:${MARKETING_EMAIL}`}>{MARKETING_EMAIL}</a>
                </>
              )}
            </div>
          ))}
        </div>
        <div className="apr-foot-bottom">
          <div>
            {`© ${new Date().getFullYear()} ${MARKETING_LEGAL_ENTITY} · `}
            {`${MARKETING_LEGAL_ADDRESS} · ${copy.hours} · `}
            <a href={`mailto:${MARKETING_EMAIL}`}>{MARKETING_EMAIL}</a>
            {' · '}
            <a href={`tel:${MARKETING_PHONE}`}>{copy.phoneHuman}</a>
            {choices && (
              <>
                {' · '}
                <a href={choices} target={outLink.target} rel={outLink.rel}>
                  {copy.privacyChoices}
                </a>
              </>
            )}
          </div>
          <div className="apr-foot-ctl">
            {isPortalI18nActive() ? (
              <PortalLanguagePill label={copy.langGroup} />
            ) : (
              <div
                className="apr-seg apr-seg--lang"
                role="group"
                aria-label={copy.langGroup}
              >
                {MARKETING_LANGUAGES.map((lang) => (
                  <a
                    key={lang.code}
                    className={lang.code === activeLang
                      ? 'apr-seg__on' : undefined}
                    aria-current={lang.code === activeLang ? 'true' : undefined}
                    href={`${base}${lang.path}`}
                    hrefLang={lang.hrefLang}
                    target={outLink.target}
                    rel={outLink.rel}
                    lang={lang.code}
                    aria-label={lang.name}
                  >
                    {lang.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
        <p className="apr-foot-ai">
          {aiBefore}
          <span className="notranslate" translate="no">
            {MARKETING_QUOTE_API_LABEL}
          </span>
          {aiAfter}
        </p>
        {/* The plan disclosure is about consumer plans: not on the fleet
            dashboard (John's test, 2026-10-28). */}
        {!onFleetDashboard && <p className="apr-foot-disclosure">{copy.disclosure}</p>}
      </div>
    </footer>
  );
}
